import cx from 'classnames';
import PropTypes from 'prop-types';

import { CUSTOM_BUTTON_COLOR, CustomButton } from 'src/components/CustomButton';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { DetectedViolation, DetectedViolationSkeleton } from 'src/components/DetectedViolation';

import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  violations: PropTypes.arrayOf(
    PropTypes.shape({
      explanation: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      formatName: PropTypes.string.isRequired,
      formatType: PropTypes.string.isRequired,
      onFixAllClick: PropTypes.func,
    })
  ).isRequired,
  loading: PropTypes.bool,
  readOnly: PropTypes.bool,
};

const defaultProps = {
  className: null,
  classes: null,
  loading: false,
  readOnly: false,
};

export const FormatComplianceIssues = ({ className, classes, violations, loading, readOnly }) => {
  const styles = useStyles({ classes });

  if (!violations.length) {
    return null;
  }

  // Group violations by format
  const violationsByFormat = violations.reduce((acc, violation) => {
    const formatKey = violation.formatType;
    if (!acc[formatKey]) {
      acc[formatKey] = {
        formatName: violation.formatName,
        violations: [],
        onFixAllClick: violation.onFixAllClick,
      };
    }
    acc[formatKey].violations.push(violation);
    return acc;
  }, {});

  if (loading) {
    return <DetectedViolationSkeleton />;
  }

  const fixText = violations.length > 1 ? 'Fix Issues' : 'Fix Issue';

  return (
    <div className={cx(styles.root, className)}>
      <CustomTypography
        className={styles.sectionTitle}
        variant={TYPOGRAPHY_VARIANT.medium14}
      >
        Compliance Issues
      </CustomTypography>
      <div className={styles.formatsList}>
        {Object.values(violationsByFormat).map((formatData, formatIndex) => (
          <div
            key={`${formatData.formatName}-${formatIndex}`}
            className={styles.complianceBox}
          >
            <CustomTypography
              className={styles.formatName}
              variant={TYPOGRAPHY_VARIANT.medium14}
            >
              {formatData.formatName}
            </CustomTypography>
            <div className={styles.violationsList}>
              {formatData.violations.map((violation, violationIndex) => (
                <DetectedViolation
                  className={styles.detectedViolation}
                  key={`${formatData.formatName}-${violationIndex}`}
                  detectedViolation={violation}
                />
              ))}
            </div>
            {!readOnly && formatData.onFixAllClick && (
              <CustomButton
                className={styles.button}
                color={CUSTOM_BUTTON_COLOR.silver}
                onClick={formatData.onFixAllClick}
              >
                {fixText}
              </CustomButton>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

FormatComplianceIssues.propTypes = propTypes;
FormatComplianceIssues.defaultProps = defaultProps;
